import React, { useEffect, useState } from 'react';
import { gsap } from 'gsap';
import Modal from './Modal';
import './Slide3.css';

const Slide3Component = () => {
  const [typedText, setTypedText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const openModal = (cardType) => {
    setModalContent(getModalContent(cardType));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const getModalContent = (type) => {
    const contents = {
      cards: {
        title: 'Cartes de Résultats',
        icon: 'bi bi-grid-3x3-gap',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-layout-text-window-reverse"></i> Une nouvelle façon de voir les résultats</h3>
              <p>
                Les cartes de résultats de MarIAne réinventent complètement l'expérience de navigation web.
                Au lieu de simples liens bleus alignés verticalement, chaque résultat est présenté sous forme
                de carte interactive riche en informations, offrant un aperçu visuel immédiat du contenu avant
                même de cliquer. C'est la fin du clic aveugle et du retour en arrière frustrant.
              </p>
              <p>
                Chaque carte affiche non seulement le titre et l'URL, mais aussi une image représentative,
                un extrait contextuel intelligent généré par l'IA, et des métadonnées pertinentes comme
                la date de publication, l'auteur, ou le type de contenu. Vous savez instantanément si
                le résultat correspond vraiment à ce que vous cherchez.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
              alt="Interface moderne de cartes"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-eye"></i> Prévisualisation intelligente</h3>
              <p>
                La prévisualisation du contenu va bien au-delà d'un simple extrait de texte. Notre IA analyse
                la page entière pour identifier et extraire les informations les plus pertinentes par rapport
                à votre requête spécifique. Si vous cherchez une recette, vous verrez les ingrédients principaux
                et le temps de préparation. Si c'est un article scientifique, vous aurez le résumé des conclusions.
              </p>
              <p>
                Les images, graphiques et vidéos importants sont également mis en avant dans la carte,
                vous donnant une vision complète du contenu en un seul coup d'œil. Fini le temps perdu
                à ouvrir dix onglets pour trouver le bon résultat.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "Voir avant de cliquer : les cartes de résultats transforment
              la recherche web en expérience visuelle intuitive où chaque élément d'information est
              immédiatement accessible et compréhensible."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-funnel"></i> Filtres intelligents</h3>
              <p>
                Les filtres de MarIAne ne sont pas de simples options statiques. Ils s'adaptent dynamiquement
                à vos résultats de recherche, vous proposant les critères de filtrage les plus pertinents
                pour votre requête spécifique. Recherchez des recettes ? Filtrez par temps de cuisson,
                difficulté, ou régime alimentaire. Recherchez des articles ? Filtrez par date, source,
                ou niveau de lecture.
              </p>
              <p>
                L'IA apprend de vos préférences de filtrage au fil du temps pour vous suggérer automatiquement
                les filtres que vous utilisez le plus souvent, rendant votre expérience de recherche
                toujours plus rapide et efficace.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800&q=80"
              alt="Organisation et filtres"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-mouse"></i> Navigation intuitive</h3>
              <p>
                L'interface a été pensée pour réduire le nombre de clics et de mouvements nécessaires.
                Les actions fréquentes comme sauvegarder un résultat, le partager, ou demander plus
                d'informations à l'IA sont accessibles directement depuis la carte, sans avoir besoin
                d'ouvrir de menus ou de naviguer ailleurs. Tout est à portée de clic.
              </p>
            </div>
          </>
        )
      },
      chat: {
        title: 'Chat IA',
        icon: 'bi bi-chat-left-dots',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-mic"></i> Parlez naturellement</h3>
              <p>
                Le chat IA de MarIAne comprend le langage humain dans toute sa richesse et sa complexité.
                Vous n'avez plus besoin de formuler des requêtes artificielles avec des mots-clés précis.
                Posez vos questions comme vous le feriez à un ami expert : "Pourquoi le ciel est bleu ?",
                "Quelle est la meilleure façon d'apprendre le piano en tant qu'adulte ?", ou même
                "Je cherche un livre qui ressemble à 1984 mais plus récent".
              </p>
              <p>
                L'assistant comprend les nuances, les sous-entendus, et même les fautes de frappe.
                Il ne se contente pas de chercher des mots-clés, il comprend votre intention et
                adapte ses réponses en conséquence. C'est une vraie conversation, pas un formulaire
                de recherche déguisé.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=800&q=80"
              alt="Conversation naturelle avec l'IA"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-reply"></i> Réponses contextuelles</h3>
              <p>
                Le chat maintient un contexte conversationnel complet. Si vous demandez "Et en France ?",
                l'IA sait exactement de quoi vous parlez en se basant sur les échanges précédents.
                Elle peut approfondir un sujet, le comparer avec un autre, ou prendre un angle différent
                selon vos demandes, le tout sans perdre le fil de la conversation.
              </p>
              <p>
                Les réponses ne sont pas des copier-coller d'articles web. L'IA synthétise l'information
                de multiples sources pour vous donner une réponse claire, complète et personnalisée.
                Si elle cite des sources, elle le fait de manière transparente pour que vous puissiez
                vérifier et approfondir par vous-même.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "Le chat IA n'est pas un moteur de recherche avec une interface
              de messagerie. C'est un véritable assistant qui comprend, raisonne et vous guide dans
              votre exploration de l'information."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-lightbulb"></i> Suggestions intelligentes</h3>
              <p>
                L'assistant ne se contente pas de répondre à vos questions. Il anticipe vos besoins
                en vous proposant des questions de suivi pertinentes, des sujets connexes à explorer,
                ou des angles différents que vous n'auriez peut-être pas envisagés. C'est comme avoir
                un guide personnel dans l'océan d'informations du web.
              </p>
              <p>
                Ces suggestions sont générées en temps réel en fonction du contexte de votre recherche
                et des informations découvertes. Elles vous permettent d'explorer un sujet en profondeur
                sans avoir à formuler vous-même chaque nouvelle requête, rendant la découverte
                d'information plus fluide et naturelle.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&q=80"
              alt="Intelligence artificielle et suggestions"
              className="modal-image"
            />
          </>
        )
      },
      summary: {
        title: 'Summary IA',
        icon: 'bi bi-file-text',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-file-earmark-text"></i> Synthèse instantanée</h3>
              <p>
                Le Summary IA est l'une des fonctionnalités les plus puissantes de MarIAne. Au lieu
                de devoir lire manuellement chaque résultat de recherche pour en extraire l'information
                pertinente, notre IA le fait pour vous en quelques secondes. Elle analyse tous les
                résultats trouvés, identifie les informations clés, et vous présente une synthèse
                cohérente et structurée.
              </p>
              <p>
                Cette synthèse n'est pas un simple copier-coller du premier résultat. L'IA croise
                les informations de multiples sources, identifie les points de consensus et les divergences,
                et vous présente une vue d'ensemble complète et nuancée du sujet. C'est comme avoir
                un assistant de recherche personnel qui lit tout pour vous et vous fait un rapport.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80"
              alt="Analyse et synthèse de documents"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-key"></i> Extraction des informations clés</h3>
              <p>
                L'IA ne se contente pas de résumer, elle extrait intelligemment les informations
                les plus importantes pour votre besoin spécifique. Si vous cherchez une réponse
                à une question précise, elle met en évidence la réponse directe avant de fournir
                le contexte. Si vous explorez un sujet large, elle structure l'information en
                sections logiques et hiérarchisées.
              </p>
              <p>
                Les dates, chiffres, noms, et concepts clés sont automatiquement identifiés et
                mis en valeur. Les définitions de termes techniques sont fournies inline, et les
                relations entre concepts sont visualisées quand c'est pertinent. L'objectif est
                de transformer un amas d'informations brutes en connaissance structurée et actionnable.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "Le Summary IA transforme des heures de lecture et de recherche
              en quelques minutes de compréhension claire. C'est l'accélérateur de connaissance
              que nous avons toujours voulu."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-diagram-2"></i> Comparaison automatique des sources</h3>
              <p>
                Une des capacités les plus puissantes du Summary IA est sa capacité à comparer
                automatiquement différentes sources. Lorsque les informations divergent ou se
                contredisent, l'IA le signale clairement et présente les différents points de vue
                de manière objective, avec leurs sources respectives.
              </p>
              <p>
                Cette fonctionnalité est particulièrement utile pour les sujets controversés,
                les actualités, ou les domaines où plusieurs écoles de pensée coexistent. Au lieu
                de vous donner une vision unique et potentiellement biaisée, MarIAne vous donne
                les outils pour comprendre la complexité du sujet et former votre propre opinion.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
              alt="Comparaison et analyse de données"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-bookmark-check"></i> Références traçables</h3>
              <p>
                Chaque élément d'information dans le Summary est traçable jusqu'à sa source originale.
                Un simple clic vous permet de vérifier l'information, d'accéder au contexte complet,
                ou d'explorer plus en profondeur. La transparence et la vérifiabilité sont au cœur
                de notre approche : vous ne devez jamais avoir à faire confiance aveuglément à l'IA.
              </p>
            </div>
          </>
        )
      }
    };

    return contents[type];
  };

  useEffect(() => {
    const tl = gsap.timeline();

    // 1. Le header SearchResults est déjà visible (copié de la slide 2)
    tl.set('.slide3-header', { opacity: 1 });

    // 2. Typewriter dans la barre de recherche (1 seconde après)
    tl.add(() => {
      const text = "Comment fonctionne la recherche sémantique ?";
      let index = 0;
      const typeInterval = setInterval(() => {
        if (index <= text.length) {
          setTypedText(text.slice(0, index));
          index++;
        } else {
          clearInterval(typeInterval);
        }
      }, 50);
    }, '+=0.8');

    // 3. Pulse du bouton search
    tl.to('.slide3-search-button', {
      scale: 1.1,
      boxShadow: '0 0 40px rgba(0, 212, 255, 0.8)',
      duration: 0.3,
      repeat: 1,
      yoyo: true
    }, '+=2');

    // 4. Le titre principal apparaît
    tl.fromTo('.slide3-main-title',
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }
    , '+=0.3');

    // 5. Ligne séparatrice
    tl.fromTo('.slide3-separator',
      { opacity: 0, scaleX: 0 },
      { opacity: 1, scaleX: 1, duration: 0.8, ease: 'power2.inOut' }
    , '-=0.4');

    // 6. Les 3 blocs de contenu apparaissent en stagger
    tl.fromTo('.interface-block',
      { opacity: 0, y: 50, scale: 0.95 },
      { opacity: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.15, ease: 'power2.out' }
    , '+=0.2');

    return () => tl.kill();
  }, []);

  return (
    <div className="slide-content slide-3-transition">
      {/* Header SearchResults - copie de slide 2 */}
      <header className="slide3-header">
        <div className="slide3-header-content">
          <div className="slide3-logo-small">
            MAR<span className="neon-text">IA</span>NE
          </div>
          <form className="slide3-search-form">
            <div className="slide3-search-box">
              <input
                type="text"
                className="slide3-search-input"
                placeholder="Rechercher..."
                value={typedText}
                readOnly
              />
              <button type="button" className="slide3-search-button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </header>

      {/* Contenu principal */}
      <div className="slide3-content">
        <div className="slide3-main-title">
          <h1 className="slide3-h1">L'Interface</h1>
          <p className="slide3-sub">Navigation Enrichie par l'Intelligence Artificielle</p>
        </div>

        <div className="slide3-separator"></div>

        <div className="interface-blocks">
          <div className="interface-block" onClick={() => openModal('cards')} style={{ cursor: 'pointer' }}>
            <div className="block-header">
              <div className="block-icon">
                <i className="bi bi-grid-3x3-gap"></i>
              </div>
              <h3 className="block-title">Cartes de Résultats</h3>
            </div>
            <div className="block-content">
              <p>Interface moderne avec aperçus visuels des résultats de recherche</p>
              <ul className="block-list">
                <li>Prévisualisation du contenu</li>
                <li>Navigation intuitive</li>
                <li>Filtres intelligents</li>
              </ul>
            </div>
          </div>

          <div className="interface-block" onClick={() => openModal('chat')} style={{ cursor: 'pointer' }}>
            <div className="block-header">
              <div className="block-icon">
                <i className="bi bi-chat-left-dots"></i>
              </div>
              <h3 className="block-title">Chat IA</h3>
            </div>
            <div className="block-content">
              <p>Assistant conversationnel pour affiner vos recherches</p>
              <ul className="block-list">
                <li>Questions en langage naturel</li>
                <li>Réponses contextuelles</li>
                <li>Suggestions intelligentes</li>
              </ul>
            </div>
          </div>

          <div className="interface-block" onClick={() => openModal('summary')} style={{ cursor: 'pointer' }}>
            <div className="block-header">
              <div className="block-icon">
                <i className="bi bi-file-text"></i>
              </div>
              <h3 className="block-title">Summary IA</h3>
            </div>
            <div className="block-content">
              <p>Synthèse automatique des résultats de recherche</p>
              <ul className="block-list">
                <li>Synthèse intelligente des résultats</li>
                <li>Extraction des informations clés</li>
                <li>Comparaison automatique des sources</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {modalContent && (
        <Modal
          isOpen={modalOpen}
          onClose={closeModal}
          title={modalContent.title}
          icon={modalContent.icon}
          content={modalContent.content}
        />
      )}
    </div>
  );
};

export default Slide3Component;
